import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import { supabase } from "@/integrations/supabase/client";
import { useLocale } from "@/i18n/LocaleProvider";
import { localizePath, parsePath } from "@/i18n/locales";
import { fetchTranslation, fetchTranslations, applyTranslation } from "@/lib/blogI18n";
import SEOHead from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { ParallaxCover } from "@/components/ParallaxCover";
import {

  Loader2,
  ArrowLeft,
  List,
  ArrowRight,
  Linkedin,
  Github,
  Twitter,
  Instagram,
  Youtube,
  Globe,
  Mail,
} from "lucide-react";

const socialIconFor = (url: string, label?: string) => {
  const haystack = `${url} ${label ?? ""}`.toLowerCase();
  if (haystack.includes("linkedin")) return Linkedin;
  if (haystack.includes("github")) return Github;
  if (haystack.includes("twitter") || haystack.includes("x.com")) return Twitter;
  if (haystack.includes("instagram")) return Instagram;
  if (haystack.includes("youtube")) return Youtube;
  if (haystack.startsWith("mailto") || haystack.includes("mailto:")) return Mail;
  return Globe;
};

import { imageSrcSet, sizedImage } from "@/lib/media";

type Post = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  cover_url: string;
  tags: string[];
  author: string;
  author_id: string | null;
  published_at: string | null;
  updated_at: string | null;
  meta_title: string;
  meta_description: string;
};

type RelatedPost = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  cover_url: string;
  tags: string[];
};

type AuthorSocial = { label: string; url: string };
type AuthorProfile = {
  name: string;
  title: string;
  bio: string;
  avatar_url: string;
  socials: AuthorSocial[];
};

type TocItem = { id: string; text: string; level: number };
type FaqItem = { question: string; answer: string };

const slugify = (text: string) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 60) || "section";

const escapeHtml = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/** Replace [[article:slug:text]] tokens with a slim inline cross-link banner. */
const renderCrossLinks = (html: string, blogBase: string, label: string): string =>
  html.replace(
    /(?:<p>\s*)?\[\[article:([a-z0-9-]+):([^\]]+)\]\](?:\s*<\/p>)?/gi,
    (_m, slug: string, text: string) => {
      const href = localizePath(`/blog/${slug.trim()}`, parsePath(blogBase).lang);
      const t = escapeHtml(text.trim());
      return `<a href="${href}" class="crosslink-banner not-prose group my-6 flex flex-col items-start gap-2 rounded-lg border border-accent/40 bg-accent/10 p-2 no-underline transition-colors hover:border-accent hover:bg-accent/20 sm:flex-row sm:items-center sm:gap-3 sm:py-2 sm:pl-2 sm:pr-3">
<span class="inline-flex shrink-0 self-start items-center rounded-md bg-accent px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-accent-foreground">${escapeHtml(label)}</span>
<span class="flex-1 text-sm font-medium leading-snug text-foreground">${t}</span>
<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="shrink-0 self-end text-accent transition-transform duration-200 ease-out group-hover:translate-x-0.5 sm:self-auto" aria-hidden="true"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
</a>`;
    }
  );

/** Parse the post HTML, inject stable ids on headings, collect a TOC + any Q&A FAQ pairs. */
const processContent = (
  html: string,
  blogBase: string,
  crossLinkLabel: string
): { html: string; toc: TocItem[]; faqs: FaqItem[] } => {
  const withLinks = html ? renderCrossLinks(html, blogBase, crossLinkLabel) : html;
  if (typeof window === "undefined" || !withLinks)
    return { html: withLinks, toc: [], faqs: [] };
  const doc = new DOMParser().parseFromString(withLinks, "text/html");
  const headings = Array.from(doc.querySelectorAll("h2, h3"));
  const toc: TocItem[] = [];
  const used = new Set<string>();

  headings.forEach((el) => {
    const text = el.textContent?.trim() ?? "";
    if (!text) return;
    const id = el.id || slugify(text);
    let unique = id;
    let i = 2;
    while (used.has(unique)) unique = `${id}-${i++}`;
    used.add(unique);
    el.id = unique;
    toc.push({ id: unique, text, level: el.tagName === "H3" ? 3 : 2 });
  });

  // Build FAQ pairs from any H2 whose text is a question.
  const faqs: FaqItem[] = [];
  const blocks = Array.from(doc.body.children);
  blocks.forEach((el, idx) => {
    if (el.tagName !== "H2") return;
    const question = el.textContent?.trim() ?? "";
    if (!question.endsWith("?")) return;
    const answerParts: string[] = [];
    for (let j = idx + 1; j < blocks.length; j++) {
      const next = blocks[j];
      if (next.tagName === "H2") break;
      const t = next.textContent?.trim();
      if (t) answerParts.push(t);
    }
    const answer = answerParts.join(" ").replace(/\s+/g, " ").trim();
    if (answer) faqs.push({ question, answer: answer.slice(0, 1200) });
  });

  return { html: doc.body.innerHTML, toc, faqs };
};

const BlogPost = () => {
  const { slug } = useParams<{ slug: string }>();
  const [activeId, setActiveId] = useState<string>("");
  const { t } = useTranslation();
  const { lang, localize } = useLocale();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["blog-post", slug, lang],
    enabled: !!slug,
    queryFn: async () => {
      let { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug!)
        .eq("status", "published")
        .maybeSingle();
      if (error) throw error;
      if (!data && lang !== "en") {
        // Localized slug (e.g. German) before the slug map has loaded.
        const { data: tr } = await (supabase as any)
          .from("blog_post_translations")
          .select("post_id")
          .eq("locale", lang)
          .eq("slug", slug!)
          .maybeSingle();
        if (tr?.post_id) {
          ({ data, error } = await supabase
            .from("blog_posts")
            .select("*")
            .eq("id", tr.post_id)
            .eq("status", "published")
            .maybeSingle());
          if (error) throw error;
        }
      }
      const post = data as Post | null;
      if (!post) return null;
      const tr = await fetchTranslation(post.id, lang);
      return applyTranslation(post, tr);
    },
  });

  // Author profile (Settings → Author) for the byline box + Person schema.
  const { data: authorProfile } = useQuery({
    queryKey: ["blog-author", data?.author_id, lang],
    enabled: !!data?.author_id,
    queryFn: async () => {
      const { data: row, error } = await supabase
        .from("authors")
        .select("name, title, bio, avatar_url, socials")
        .eq("id", data!.author_id!)
        .eq("published", true)
        .maybeSingle();
      if (error) throw error;
      if (!row) return null;
      let title = row.title;
      let bio = row.bio;
      if (lang !== "en") {
        const { data: tr } = await supabase
          .from("author_translations")
          .select("title, bio")
          .eq("author_id", data!.author_id!)
          .eq("locale", lang)
          .maybeSingle();
        if (tr?.title?.trim()) title = tr.title;
        if (tr?.bio?.trim()) bio = tr.bio;
      }
      return {
        ...row,
        title,
        bio,
        socials: Array.isArray(row.socials)
          ? (row.socials as unknown as AuthorSocial[]).filter((s) => s?.url)
          : [],
      } as AuthorProfile;
    },
  });




  // Related posts that share at least one tag with the current article.
  const { data: related } = useQuery({
    queryKey: ["related-posts", data?.id, data?.tags, lang],
    enabled: !!data?.id,
    queryFn: async () => {
      const tags = data?.tags ?? [];
      let query = supabase
        .from("blog_posts")
        .select("id, title, slug, excerpt, cover_url, tags")
        .eq("status", "published")
        .neq("id", data!.id)
        .order("published_at", { ascending: false })
        .limit(3);
      if (tags.length > 0) query = query.overlaps("tags", tags);
      const { data: rows, error } = await query;
      if (error) throw error;
      let result = (rows as RelatedPost[]) ?? [];
      // Fall back to most recent posts if no tag matches were found.
      if (result.length === 0) {
        const { data: recent } = await supabase
          .from("blog_posts")
          .select("id, title, slug, excerpt, cover_url, tags")
          .eq("status", "published")
          .neq("id", data!.id)
          .order("published_at", { ascending: false })
          .limit(3);
        result = (recent as RelatedPost[]) ?? [];
      }
      // Localize related post titles/excerpts.
      const translations = await fetchTranslations(result.map((r) => r.id), lang);
      return result.map((r) => applyTranslation(r, translations.get(r.id)));
    },
  });


  const blogBase = localize("/blog");
  const crossLinkLabel = t("blogPage.keepReading");
  const { html: safeHtml, toc, faqs } = useMemo(() => {
    const raw = data?.content || "";
    const { html, toc, faqs } = processContent(raw, blogBase, crossLinkLabel);
    return { html: DOMPurify.sanitize(html), toc, faqs };
  }, [data?.content, blogBase, crossLinkLabel]);

  // Split the article at the <h2> closest to the middle so a single
  // mid-article conversion block can sit between two real sections.
  const [htmlTop, htmlBottom] = useMemo(() => {
    if (safeHtml.length < 2500) return [safeHtml, ""];
    const marks: number[] = [];
    const re = /<h2[\s>]/gi;
    let m: RegExpExecArray | null;
    while ((m = re.exec(safeHtml))) marks.push(m.index);
    const target = safeHtml.length * 0.5;
    const candidates = marks.filter((i) => i > safeHtml.length * 0.25 && i < safeHtml.length * 0.8);
    if (!candidates.length) return [safeHtml, ""];
    const cut = candidates.reduce((a, b) => (Math.abs(b - target) < Math.abs(a - target) ? b : a));
    return [safeHtml.slice(0, cut), safeHtml.slice(cut)];
  }, [safeHtml]);


  // Scroll-spy to highlight the active TOC entry.
  useEffect(() => {
    if (toc.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-120px 0px -70% 0px", threshold: 0 }
    );
    toc.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [toc, safeHtml]);

  const handleTocClick = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (!el) return;
    const offset = 100;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: "smooth" });
    history.replaceState(null, "", `#${id}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="animate-spin text-accent" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="container pt-40 pb-32 text-center">
          <p className="mono-label mb-4">// 404</p>
          <h1 className="text-3xl font-bold text-foreground">{t("blogPage.notFound")}</h1>
          <Link to={localize("/blog")} className="text-accent hover:underline mt-6 inline-block">
            {t("blogPage.backToBlog")}
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const BASE_URL = "https://devcore.systems";
  // Self-referential, locale-aware canonical used across the JSON-LD graph.
  const canonical = `${BASE_URL}${localize(`/blog/${data.slug}`)}`;
  const author = data.author || "The DevCore Team";

  // Cross-linked articles ([[article:slug:text]]) become machine-readable relations.
  const crossLinkedUrls = Array.from(
    new Set(
      Array.from((data.content || "").matchAll(/\[\[article:([a-z0-9-]+):/gi)).map(
        (m) => `${BASE_URL}${localize(`/blog/${m[1]}`)}`
      )
    )
  ).filter((url) => url !== canonical);

  const wordCount = (data.content || "")
    .replace(/<[^>]*>/g, " ")
    .split(/\s+/)
    .filter(Boolean).length;

  // Stable Person node so every article points at the same author entity.
  const personId = authorProfile
    ? `${BASE_URL}/#person-${slugify(authorProfile.name)}`
    : undefined;
  const primaryProfile = authorProfile?.socials.find((s) =>
    /linkedin\.com/i.test(s.url)
  )?.url;

  const publisherNode = {
    "@type": "Organization",
    "@id": `${BASE_URL}/#organization`,
    name: "DevCore",
    url: BASE_URL,
    sameAs: ["https://www.linkedin.com/company/devcore-websystems"],
    logo: {
      "@type": "ImageObject",
      url: "https://devcore.systems/favicon.png",
    },
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    inLanguage: lang,
    headline: data.title,
    description: data.meta_description || data.excerpt,
    image: data.cover_url ? [data.cover_url] : undefined,
    datePublished: data.published_at ?? undefined,
    dateModified: data.updated_at || data.published_at || undefined,
    wordCount: wordCount || undefined,
    articleSection: data.tags?.length ? data.tags[0] : undefined,
    keywords: data.tags?.length ? data.tags.join(", ") : undefined,
    author: authorProfile
      ? {
          "@type": "Person",
          "@id": personId,
          name: authorProfile.name,
          url: primaryProfile || `${canonical}#author`,
          jobTitle: authorProfile.title || undefined,
          description: authorProfile.bio || undefined,
          image: authorProfile.avatar_url || undefined,
          sameAs:
            authorProfile.socials.length > 0
              ? authorProfile.socials.map((s) => s.url)
              : undefined,
          worksFor: { "@id": `${BASE_URL}/#organization` },
        }
      : { "@type": "Person", name: author },
    publisher: publisherNode,
    isPartOf: {
      "@type": "Blog",
      "@id": `${BASE_URL}${localize("/blog")}#blog`,
      name: "DevCore Blog",
      url: `${BASE_URL}${localize("/blog")}`,
    },
    citation: crossLinkedUrls.length > 0 ? crossLinkedUrls : undefined,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": canonical,
      url: canonical,
      relatedLink: crossLinkedUrls.length > 0 ? crossLinkedUrls : undefined,
    },
  };


  // Breadcrumb so Google can render Home › Blog › Post jump links.
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: t("blogPage.home"), item: `${BASE_URL}${localize("/")}` },
      { "@type": "ListItem", position: 2, name: t("nav.blog"), item: `${BASE_URL}${localize("/blog")}` },
      { "@type": "ListItem", position: 3, name: data.title, item: canonical },
    ],
  };


  // Table-of-contents schema so Google can surface in-page jump links.
  const tocSchema =
    toc.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: `Table of contents — ${data.title}`,
          itemListOrder: "https://schema.org/ItemListOrderAscending",
          itemListElement: toc.map((item, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: item.text,
            url: `${canonical}#${item.id}`,
          })),
        }
      : null;

  // FAQ schema generated from question-style H2 headings.
  const faqSchema =
    faqs.length >= 2
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.question,
            acceptedAnswer: { "@type": "Answer", text: f.answer },
          })),
        }
      : null;

  const jsonLd: Record<string, unknown>[] = [articleSchema, breadcrumbSchema];
  if (tocSchema) jsonLd.push(tocSchema);
  if (faqSchema) jsonLd.push(faqSchema);

  return (
    <div className="min-h-screen">
      <SEOHead
        title={`${data.meta_title || data.title} | DevCore`}
        description={data.meta_description || data.excerpt}
        canonical={canonical}
        ogType="article"
        ogImage={data.cover_url || undefined}
        jsonLd={jsonLd}
      />
      <Navbar />

      <article className="relative pt-16 md:pt-20 pb-24">
        <div className="absolute inset-0 tech-grid opacity-[0.08] pointer-events-none" />

        {/* Full-width, edge-to-edge cover image at the very top */}
        {data.cover_url && (
          <ParallaxCover
            src={sizedImage(data.cover_url, 1600, 74)}
            srcSet={`${sizedImage(data.cover_url, 1000, 74)} 1000w, ${sizedImage(data.cover_url, 1600, 74)} 1600w`}
            sizes="100vw"
            alt={data.title}
            className="h-[200px] sm:h-[340px] md:h-[440px]"
          />
        )}


        <div className="container relative z-10">
          {/* Breadcrumb trail (also reinforces internal linking) */}
          <nav aria-label="Breadcrumb" className="mb-6 mt-8 md:mb-8 md:mt-12">
            <ol className="flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground md:text-sm">
              <li>
                <Link to={localize("/")} className="hover:text-accent transition-colors">
                  {t("blogPage.home")}
                </Link>
              </li>
              <li aria-hidden className="text-border">/</li>
              <li>
                <Link to={localize("/blog")} className="hover:text-accent transition-colors">
                  {t("nav.blog")}
                </Link>
              </li>
              <li aria-hidden className="hidden text-border sm:block">/</li>
              <li className="hidden max-w-[50vw] truncate text-foreground sm:block">{data.title}</li>
            </ol>
          </nav>

          <h1 className="max-w-[22ch] text-[1.75rem] leading-[1.12] tracking-[-0.02em] font-bold text-foreground text-balance sm:text-[2rem] sm:leading-[1.06] md:text-[3.25rem]">
            {data.title}
          </h1>

          {data.excerpt && (
            <p className="mt-4 max-w-[58ch] text-[15px] leading-relaxed text-muted-foreground text-pretty sm:mt-5 sm:text-lg md:text-xl">
              {data.excerpt}
            </p>
          )}

          <div className="mt-6 flex flex-col gap-3 border-t border-border/70 pt-5 text-sm sm:mt-7 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-5">
            {authorProfile ? (
              <a
                href="#author"
                className="group flex min-w-0 items-center gap-3 transition-colors"
              >
                {authorProfile.avatar_url && (
                  <img
                    {...imageSrcSet(authorProfile.avatar_url, 72, 82)}
                    alt={authorProfile.name}
                    width={36}
                    height={36}
                    loading="lazy"
                    decoding="async"
                    className="h-9 w-9 shrink-0 rounded-full object-cover border border-border"
                  />
                )}
                <span className="flex min-w-0 flex-col leading-tight text-left">
                  <span className="truncate font-medium text-foreground transition-colors group-hover:text-accent">
                    {authorProfile.name}
                  </span>
                  {authorProfile.title && (
                    <span className="truncate text-xs text-muted-foreground">{authorProfile.title}</span>
                  )}
                </span>
              </a>
            ) : (
              <span className="font-medium text-foreground">{author}</span>
            )}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 sm:ml-auto sm:flex-col sm:items-end sm:gap-1 sm:text-right">
              {data.published_at && (
                <time
                  dateTime={data.published_at}
                  className="font-mono text-xs tabular-nums text-muted-foreground"
                >
                  {new Date(data.published_at).toLocaleDateString(lang, {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </time>
              )}
              {data.tags?.length > 0 && (
                <span className="font-mono text-xs text-muted-foreground/80">
                  /{data.tags.slice(0, 2).join(" /")}
                </span>
              )}
            </div>

          </div>





          <div className="mt-10 md:mt-14 lg:grid lg:grid-cols-[minmax(0,1fr)_300px] lg:gap-14 xl:gap-20">
            <div>
              <div
                className="prose prose-invert prose-base md:prose-lg max-w-none prose-headings:font-bold prose-headings:tracking-[-0.015em] prose-headings:scroll-mt-28 prose-h2:mt-10 prose-h2:text-[1.3rem] md:prose-h2:mt-14 md:prose-h2:text-[1.6rem] prose-h3:mt-8 prose-h3:text-[1.1rem] md:prose-h3:mt-10 md:prose-h3:text-[1.25rem] prose-p:leading-[1.7] prose-p:max-w-[68ch] prose-li:max-w-[66ch] prose-a:text-accent prose-a:decoration-accent/40 prose-a:underline-offset-4 prose-strong:text-foreground prose-blockquote:border-l-accent prose-blockquote:not-italic prose-blockquote:text-foreground"
                dangerouslySetInnerHTML={{ __html: htmlTop }}
              />

              {/* Mid-article bridge: turns the argument into a self-check + next step */}
              {htmlBottom && (
                <aside className="my-11 max-w-[68ch] border-y border-border py-7 md:my-14 md:py-8">
                  <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-accent">
                    // {t("blogPage.midLabel")}
                  </p>
                  <p className="mt-3 text-base font-bold leading-snug text-foreground md:text-lg">
                    {t("blogPage.midTitle")}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {t("blogPage.midText")}
                  </p>
                  <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
                    <Link
                      to={localize("/blueprint")}
                      className="inline-flex min-h-11 items-center gap-2 text-sm font-medium text-accent underline decoration-accent/40 underline-offset-4 transition-colors hover:decoration-accent"
                    >
                      {t("blogPage.midBlueprint")} <ArrowRight size={14} />
                    </Link>
                    <Link
                      to={localize("/contact")}
                      className="inline-flex min-h-11 items-center text-sm text-muted-foreground underline decoration-border underline-offset-4 transition-colors hover:text-foreground hover:decoration-accent"
                    >
                      {t("blogPage.midTalk")}
                    </Link>
                  </div>
                </aside>
              )}

              {htmlBottom && (
                <div
                  className="prose prose-invert prose-base md:prose-lg max-w-none prose-headings:font-bold prose-headings:tracking-[-0.015em] prose-headings:scroll-mt-28 prose-h2:mt-10 prose-h2:text-[1.3rem] md:prose-h2:mt-14 md:prose-h2:text-[1.6rem] prose-h3:mt-8 prose-h3:text-[1.1rem] md:prose-h3:mt-10 md:prose-h3:text-[1.25rem] prose-p:leading-[1.7] prose-p:max-w-[68ch] prose-li:max-w-[66ch] prose-a:text-accent prose-a:decoration-accent/40 prose-a:underline-offset-4 prose-strong:text-foreground prose-blockquote:border-l-accent prose-blockquote:not-italic prose-blockquote:text-foreground"
                  dangerouslySetInnerHTML={{ __html: htmlBottom }}
                />
              )}


              {/* Author — E-E-A-T signal, mirrors the Person schema */}
              {authorProfile && (
                <div
                  id="author"
                  className="mt-12 scroll-mt-28 border-t border-border pt-7 md:mt-16 md:pt-8"
                >
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6">
                    {authorProfile.avatar_url && (
                      <img
                        {...imageSrcSet(authorProfile.avatar_url, 200, 82)}
                        alt={authorProfile.name}
                        width={96}
                        height={96}
                        loading="lazy"
                        decoding="async"
                        sizes="96px"
                        className="h-16 w-16 shrink-0 rounded-full object-cover sm:h-24 sm:w-24"
                      />
                    )}
                    <div className="min-w-0">
                      <p className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground/70">
                        {t("blogPage.aboutAuthor")}
                      </p>
                      <p className="mt-2 text-lg font-bold text-foreground">
                        {authorProfile.name}
                      </p>
                      {authorProfile.title && (
                        <p className="mt-0.5 font-mono text-xs text-accent">
                          {authorProfile.title}
                        </p>
                      )}

                      {authorProfile.bio && (
                        <p className="mt-3 max-w-[62ch] text-sm leading-relaxed text-muted-foreground">
                          {authorProfile.bio}
                        </p>
                      )}
                      {authorProfile.socials.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-4">
                          {authorProfile.socials.map((s) => {
                            const Icon = socialIconFor(s.url, s.label);
                            return (
                              <a
                                key={s.url}
                                href={s.url}
                                target="_blank"
                                rel="me noopener noreferrer"
                                className="inline-flex items-center gap-2 font-mono text-xs text-muted-foreground underline decoration-border underline-offset-4 transition-colors hover:text-accent hover:decoration-accent"
                              >
                                <Icon className="h-3.5 w-3.5" aria-hidden />
                                {s.label || s.url.replace(/^https?:\/\/(www\.)?/, "")}
                              </a>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* In-content CTA — internal link to the conversion path */}
              <div className="mt-12 -mx-4 rounded-none bg-accent/[0.07] px-4 py-7 sm:mx-0 sm:rounded-lg sm:px-6 md:mt-16 md:flex md:items-end md:justify-between md:gap-10 md:px-9 md:py-9">
                <div className="max-w-[42ch]">
                  <h2 className="text-lg font-bold leading-snug text-foreground sm:text-xl md:text-2xl">
                    {t("blogPage.ctaTitle")}
                  </h2>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {t("blogPage.ctaText")}
                  </p>
                </div>
                <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-3 md:mt-0 md:shrink-0">
                  <Link
                    to={localize("/contact")}
                    className="inline-flex min-h-11 items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-transform duration-150 ease-out active:scale-[0.97]"
                  >
                    {t("blogPage.ctaTalk")} <ArrowRight size={15} />
                  </Link>
                  <Link
                    to={localize("/blueprint")}
                    className="text-sm font-medium text-accent underline decoration-accent/40 underline-offset-4 transition-colors hover:decoration-accent"
                  >
                    {t("blogPage.ctaBlueprint")}
                  </Link>

                </div>
              </div>
            </div>


            {toc.length > 0 && (
              <aside className="hidden lg:block">
                <nav aria-label="Table of contents" className="sticky top-28">
                  <p className="mb-4 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground/70">
                    <List size={12} /> {t("blogPage.contents")}
                  </p>
                  <ul className="space-y-0.5 border-l border-border/70">
                    {toc.map((item) => (
                      <li key={item.id}>
                        <a
                          href={`#${item.id}`}
                          onClick={(e) => handleTocClick(e, item.id)}
                          className={`-ml-px block border-l py-1.5 text-sm leading-snug transition-colors duration-150 ease-out ${
                            item.level === 3 ? "pl-6 text-[13px]" : "pl-4"
                          } ${
                            activeId === item.id
                              ? "border-accent text-accent"
                              : "border-transparent text-muted-foreground hover:text-foreground"
                          }`}
                        >
                          {item.text}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </aside>
            )}
          </div>

          {/* Related posts — internal linking + lower bounce */}
          {related && related.length > 0 && (
            <section className="mt-16 border-t border-border pt-8 md:mt-24 md:pt-10" aria-label="Related articles">
              <p className="mb-6 font-mono text-[11px] uppercase tracking-[0.18em] text-muted-foreground/70 md:mb-8">
                {t("blogPage.keepReading")}
              </p>
              <div className="grid gap-8 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:gap-16">
                {related.slice(0, 1).map((post) => (
                  <Link key={post.id} to={localize(`/blog/${post.slug}`)} className="group block">
                    {post.cover_url && (
                      <div className="overflow-hidden rounded-lg">
                        <img
                          {...imageSrcSet(post.cover_url, 800, 74)}
                          alt={post.title}
                          width={800}
                          height={420}
                          loading="lazy"
                          decoding="async"
                          sizes="(min-width: 1024px) 48vw, 92vw"
                          className="h-40 w-full object-cover transition-transform duration-500 [transition-timing-function:cubic-bezier(0.23,1,0.32,1)] group-hover:scale-[1.03] sm:h-52 md:h-64"
                        />
                      </div>
                    )}
                    {post.tags?.length > 0 && (
                      <p className="mt-4 font-mono text-[11px] text-muted-foreground/80">
                        /{post.tags[0]}
                      </p>
                    )}
                    <h3 className="mt-2 max-w-[30ch] text-lg font-bold leading-snug text-foreground transition-colors group-hover:text-accent sm:text-xl md:text-2xl">
                      {post.title}
                    </h3>
                    <p className="mt-2 max-w-[52ch] text-sm leading-relaxed text-muted-foreground line-clamp-2">
                      {post.excerpt}
                    </p>
                  </Link>
                ))}

                {related.length > 1 && (
                  <ul className="divide-y divide-border/70 border-t border-border/70 lg:border-t-0 lg:pt-0">
                    {related.slice(1).map((post) => (
                      <li key={post.id}>
                        <Link
                          to={localize(`/blog/${post.slug}`)}
                          className="group flex items-start gap-4 py-5"
                        >
                          <div className="min-w-0 flex-1">
                            {post.tags?.length > 0 && (
                              <p className="font-mono text-[11px] text-muted-foreground/80">
                                /{post.tags[0]}
                              </p>
                            )}
                            <h3 className="mt-1 text-base font-semibold leading-snug text-foreground transition-colors group-hover:text-accent">
                              {post.title}
                            </h3>
                            <p className="mt-1 text-sm leading-relaxed text-muted-foreground line-clamp-2">
                              {post.excerpt}
                            </p>
                          </div>
                          <ArrowRight
                            size={16}
                            aria-hidden
                            className="mt-1 shrink-0 text-muted-foreground/60 transition-all duration-200 ease-out group-hover:translate-x-0.5 group-hover:text-accent"
                          />
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          )}

        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPost;
